import React, { memo } from 'react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Server, Database, Cpu, HardDrive, Activity, Zap, Shield, Globe, CheckCircle, AlertTriangle, TrendingUp, Clock, Users, Package } from 'lucide-react';
export function SystemHealth() {
  const servers = [{
    name: 'API Server (Primary)',
    location: 'AWS Singapore',
    status: 'healthy',
    uptime: 99.98,
    cpu: 45,
    memory: 62,
    disk: 38,
    requests: 12500,
    latency: 145
  }, {
    name: 'Database Server',
    location: 'AWS Singapore',
    status: 'healthy',
    uptime: 99.97,
    cpu: 38,
    memory: 71,
    disk: 52,
    connections: 245,
    latency: 12
  }, {
    name: 'Cache Server (Redis)',
    location: 'AWS Singapore',
    status: 'healthy',
    uptime: 99.99,
    cpu: 22,
    memory: 45,
    disk: 15,
    hitRate: 94.5,
    latency: 3
  }];
  const services = [{
    name: 'Authentication Service',
    status: 'operational',
    uptime: 99.99,
    requests: 8500
  }, {
    name: 'Order Processing',
    status: 'operational',
    uptime: 99.95,
    requests: 12000
  }, {
    name: 'Payment Gateway',
    status: 'operational',
    uptime: 99.92,
    requests: 3200
  }, {
    name: 'Logistics API',
    status: 'operational',
    uptime: 99.88,
    requests: 8500
  }, {
    name: 'Notification Service',
    status: 'operational',
    uptime: 99.96,
    requests: 15000
  }, {
    name: 'Analytics Engine',
    status: 'operational',
    uptime: 99.94,
    requests: 5000
  }];
  const securityMetrics = [{
    metric: 'Failed Login Attempts',
    value: 23,
    status: 'normal',
    threshold: '< 100/hour'
  }, {
    metric: 'API Rate Limit Hits',
    value: 12,
    status: 'normal',
    threshold: '< 50/hour'
  }, {
    metric: 'Suspicious Activities',
    value: 0,
    status: 'good',
    threshold: '0'
  }, {
    metric: 'SSL Certificate',
    value: 'Valid',
    status: 'good',
    expiresIn: '287 days'
  }, {
    metric: 'Firewall Rules',
    value: 'Active',
    status: 'good',
    rules: 45
  }, {
    metric: 'DDoS Protection',
    value: 'Enabled',
    status: 'good',
    provider: 'Cloudflare'
  }];
  return <div className="space-y-6" data-id="element-3320">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3321">
        <div data-id="element-3322">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-3323">
            System Health Monitor
          </h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-3324">
            Real-time infrastructure & security monitoring
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg" data-id="element-3325">
          <Activity className="w-4 h-4 text-green-600 animate-pulse" data-id="element-3326" />
          <span className="text-sm font-bold text-green-700" data-id="element-3327">
            All Systems Operational
          </span>
        </div>
      </div>

      {/* Overall Health KPIs */}
      <div className="grid grid-cols-4 gap-4" data-id="element-3328">
        <Card className="p-5 bg-gradient-to-br from-green-50 to-green-100 border-green-200" data-id="element-3329">
          <div className="flex items-center justify-between mb-3" data-id="element-3330">
            <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center shadow-lg" data-id="element-3331">
              <CheckCircle className="w-6 h-6 text-white" data-id="element-3332" />
            </div>
            <Badge className="bg-green-200 text-green-800 border-0" data-id="element-3333">
              Excellent
            </Badge>
          </div>
          <p className="text-sm text-green-700 mb-1" data-id="element-3334">System Uptime</p>
          <p className="text-4xl font-bold text-green-900" data-id="element-3335">99.97%</p>
          <p className="text-xs text-green-600 mt-1" data-id="element-3336">Last 30 days</p>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200" data-id="element-3337">
          <div className="flex items-center justify-between mb-3" data-id="element-3338">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg" data-id="element-3339">
              <Zap className="w-6 h-6 text-white" data-id="element-3340" />
            </div>
            <Badge className="bg-blue-200 text-blue-800 border-0" data-id="element-3341">Fast</Badge>
          </div>
          <p className="text-sm text-blue-700 mb-1" data-id="element-3342">Avg Response Time</p>
          <p className="text-4xl font-bold text-blue-900" data-id="element-3343">145ms</p>
          <p className="text-xs text-blue-600 mt-1" data-id="element-3344">Target: &lt;200ms</p>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-violet-50 to-violet-100 border-violet-200" data-id="element-3345">
          <div className="flex items-center justify-between mb-3" data-id="element-3346">
            <div className="w-12 h-12 bg-violet-600 rounded-xl flex items-center justify-center shadow-lg" data-id="element-3347">
              <Shield className="w-6 h-6 text-white" data-id="element-3348" />
            </div>
            <Badge className="bg-violet-200 text-violet-800 border-0" data-id="element-3349">A+</Badge>
          </div>
          <p className="text-sm text-violet-700 mb-1" data-id="element-3350">Security Score</p>
          <p className="text-4xl font-bold text-violet-900" data-id="element-3351">98/100</p>
          <p className="text-xs text-violet-600 mt-1" data-id="element-3352">Last audit: 2 days ago</p>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200" data-id="element-3353">
          <div className="flex items-center justify-between mb-3" data-id="element-3354">
            <div className="w-12 h-12 bg-amber-600 rounded-xl flex items-center justify-center shadow-lg" data-id="element-3355">
              <Activity className="w-6 h-6 text-white" data-id="element-3356" />
            </div>
            <Badge className="bg-amber-200 text-amber-800 border-0" data-id="element-3357">High</Badge>
          </div>
          <p className="text-sm text-amber-700 mb-1" data-id="element-3358">Active Users</p>
          <p className="text-4xl font-bold text-amber-900" data-id="element-3359">1,247</p>
          <p className="text-xs text-amber-600 mt-1" data-id="element-3360">Peak: 1,850 today</p>
        </Card>
      </div>

      {/* Server Status */}
      <Card className="p-6" data-id="element-3361">
        <h3 className="font-bold text-lg text-slate-900 mb-6" data-id="element-3362">
          Server Infrastructure
        </h3>
        <div className="space-y-4" data-id="element-3363">
          {servers.map((server, i) => <div key={i} className="p-5 bg-slate-50 rounded-xl border border-slate-200" data-id="element-3364">
              <div className="flex items-start justify-between mb-4" data-id="element-3365">
                <div className="flex items-center gap-3" data-id="element-3366">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-3367">
                    <Server className="w-6 h-6 text-green-600" data-id="element-3368" />
                  </div>
                  <div data-id="element-3369">
                    <h4 className="font-bold text-slate-900" data-id="element-3370">{server.name}</h4>
                    <div className="flex items-center gap-2 text-sm text-slate-600 mt-1" data-id="element-3371">
                      <Globe className="w-4 h-4" data-id="element-3372" />
                      <span data-id="element-3373">{server.location}</span>
                    </div>
                  </div>
                </div>
                <Badge variant="success" data-id="element-3374">Healthy</Badge>
              </div>

              <div className="grid grid-cols-5 gap-4" data-id="element-3375">
                <div data-id="element-3376">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-3377">Uptime</p>
                  <p className="text-lg font-bold text-green-600" data-id="element-3378">
                    {server.uptime}%
                  </p>
                </div>
                <div data-id="element-3379">
                  <p className="text-xs text-slate-500 mb-2" data-id="element-3380">CPU Usage</p>
                  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden" data-id="element-3381">
                    <div className="h-full bg-blue-500 rounded-full" style={{
                  width: `${server.cpu}%`
                }} data-id="element-3382"></div>
                  </div>
                  <p className="text-xs text-slate-600 mt-1" data-id="element-3383">{server.cpu}%</p>
                </div>
                <div data-id="element-3384">
                  <p className="text-xs text-slate-500 mb-2" data-id="element-3385">Memory</p>
                  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden" data-id="element-3386">
                    <div className="h-full bg-violet-500 rounded-full" style={{
                  width: `${server.memory}%`
                }} data-id="element-3387"></div>
                  </div>
                  <p className="text-xs text-slate-600 mt-1" data-id="element-3388">
                    {server.memory}%
                  </p>
                </div>
                <div data-id="element-3389">
                  <p className="text-xs text-slate-500 mb-2" data-id="element-3390">Disk</p>
                  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden" data-id="element-3391">
                    <div className="h-full bg-amber-500 rounded-full" style={{
                  width: `${server.disk}%`
                }} data-id="element-3392"></div>
                  </div>
                  <p className="text-xs text-slate-600 mt-1" data-id="element-3393">{server.disk}%</p>
                </div>
                <div data-id="element-3394">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-3395">Latency</p>
                  <p className="text-lg font-bold text-slate-900" data-id="element-3396">
                    {server.latency}ms
                  </p>
                </div>
              </div>
            </div>)}
        </div>
      </Card>

      {/* Services Status */}
      <div className="grid grid-cols-2 gap-6" data-id="element-3397">
        <Card className="p-6" data-id="element-3398">
          <h3 className="font-bold text-lg text-slate-900 mb-4" data-id="element-3399">
            Microservices Status
          </h3>
          <div className="space-y-3" data-id="element-3400">
            {services.map((service, i) => <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-id="element-3401">
                <div className="flex items-center gap-3" data-id="element-3402">
                  <CheckCircle className="w-5 h-5 text-green-600" data-id="element-3403" />
                  <div data-id="element-3404">
                    <p className="text-sm font-medium text-slate-900" data-id="element-3405">
                      {service.name}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-3406">
                      {service.requests.toLocaleString()} req/min
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-3407">
                  <Badge variant="success" className="mb-1" data-id="element-3408">
                    Operational
                  </Badge>
                  <p className="text-xs text-slate-600" data-id="element-3409">
                    {service.uptime}% uptime
                  </p>
                </div>
              </div>)}
          </div>
        </Card>

        <Card className="p-6" data-id="element-3410">
          <h3 className="font-bold text-lg text-slate-900 mb-4" data-id="element-3411">
            Security Metrics
          </h3>
          <div className="space-y-3" data-id="element-3412">
            {securityMetrics.map((metric, i) => <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-id="element-3413">
                <div className="flex items-center gap-3" data-id="element-3414">
                  <Shield className={`w-5 h-5 ${metric.status === 'good' ? 'text-green-600' : 'text-blue-600'}`} data-id="element-3415" />
                  <div data-id="element-3416">
                    <p className="text-sm font-medium text-slate-900" data-id="element-3417">
                      {metric.metric}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-3418">
                      {metric.threshold || metric.expiresIn || `${metric.rules} rules`}
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-3419">
                  <p className="text-sm font-bold text-slate-900" data-id="element-3420">
                    {metric.value}
                  </p>
                  <Badge variant={metric.status === 'good' ? 'success' : 'neutral'} className="text-xs" data-id="element-3421">
                    {metric.status === 'good' ? 'Secure' : 'Normal'}
                  </Badge>
                </div>
              </div>)}
          </div>
        </Card>
      </div>

      {/* Recent Incidents */}
      <Card className="p-6" data-id="element-3422">
        <h3 className="font-bold text-lg text-slate-900 mb-4" data-id="element-3423">
          Recent System Events
        </h3>
        <div className="space-y-3" data-id="element-3424">
          <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg" data-id="element-3425">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-3426" />
            <div className="flex-1" data-id="element-3427">
              <h4 className="text-sm font-bold text-green-800" data-id="element-3428">
                System Update Completed
              </h4>
              <p className="text-xs text-green-600 mt-1" data-id="element-3429">
                All servers updated to v2.4.1 successfully. Zero downtime
                deployment.
              </p>
            </div>
            <span className="text-xs text-green-600" data-id="element-3430">2 hours ago</span>
          </div>

          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-3431">
            <Activity className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-3432" />
            <div className="flex-1" data-id="element-3433">
              <h4 className="text-sm font-bold text-blue-800" data-id="element-3434">
                High Traffic Detected
              </h4>
              <p className="text-xs text-blue-600 mt-1" data-id="element-3435">
                Traffic spike to 1,850 concurrent users. Auto-scaling triggered
                successfully.
              </p>
            </div>
            <span className="text-xs text-blue-600" data-id="element-3436">4 hours ago</span>
          </div>

          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-3437">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-3438" />
            <div className="flex-1" data-id="element-3439">
              <h4 className="text-sm font-bold text-amber-800" data-id="element-3440">
                Database Backup Completed
              </h4>
              <p className="text-xs text-amber-600 mt-1" data-id="element-3441">
                Automated daily backup completed. Backup size: 2.4GB. Stored in
                S3.
              </p>
            </div>
            <span className="text-xs text-amber-600" data-id="element-3442">6 hours ago</span>
          </div>
        </div>
      </Card>
    </div>;
}